import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Ban, CircleCheck, Languages, Mail, MessagesSquare, Phone, User } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import {
  apiSupportBlock,
  apiSupportMessageSend,
  apiSupportUnblock,
  useAdminChatSocket,
  useSupportConversation,
} from '@/entities/support-chat';
import {
  FeaturesSupportChatMessageBubble,
  FeaturesSupportChatMessageComposer,
} from '@/features/support-chat';
import type { ApiSupportMessage } from '@/shared/types/api/support-chat';

export const WidgetsSupportChatChatDetail = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const { userId = '' } = useParams<{ userId: string }>();
  const canRespond = usePerm('support.respond');
  // No dedicated block/unblock perms exist in the registry; the backend gates
  // both actions on support.respond, so mirror that here.
  const canBlock = canRespond;
  const canUnblock = canRespond;

  const query = useSupportConversation(userId);
  const [messages, setMessages] = useState<ApiSupportMessage[]>([]);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string | null>(null);

  const scrollToBottom = useCallback((): void => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Seed local state from the detail fetch; opening marks user messages read,
  // so refresh the conversations list to clear the unread badge.
  useEffect(() => {
    if (!query.data) return;
    setMessages(query.data.messages);
    setIsBlocked(query.data.is_blocked);
    const last = query.data.messages.at(-1);
    lastIdRef.current = last ? last.id : null;
    void qc.invalidateQueries({ queryKey: queryKeys.supportChat.conversationsList });
    void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Live message delivery over WebSocket (replaces the old polling). The hook
  // opens a socket for this conversation while mounted and tears it down /
  // reopens when `userId` changes; on (re)connect it refetches the detail to
  // backfill. Append incoming messages into local state, deduped by id.
  const handleIncoming = useCallback((msg: ApiSupportMessage): void => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    lastIdRef.current = msg.id;
  }, []);
  useAdminChatSocket(userId, handleIncoming);

  const send = useMutate({
    api: (payload: { text?: string; file?: File }) => apiSupportMessageSend(userId, payload),
    onSuccess: (msg) => {
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      lastIdRef.current = msg.id;
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.conversationsList });
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    },
    onError: (_e, m) => toast.error(m),
  });

  const block = useMutate({
    api: () => apiSupportBlock(userId),
    onSuccess: (res) => {
      setIsBlocked(res.is_blocked);
      setBlockConfirm(false);
      toast.success(t('support.chat.toast.blocked'));
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.conversationsList });
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    },
    onError: (_e, m) => {
      setBlockConfirm(false);
      toast.error(m);
    },
  });

  const unblock = useMutate({
    api: () => apiSupportUnblock(userId),
    onSuccess: (res) => {
      setIsBlocked(res.is_blocked);
      toast.success(t('support.chat.toast.unblocked'));
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.conversationsList });
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    },
    onError: (_e, m) => toast.error(m),
  });

  if (query.isLoading) {
    return (
      <>
        <PageHeader title={t('support.chat.title')} showBack backTo="/support" />
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  if (query.error || !query.data) {
    return (
      <>
        <PageHeader title={t('support.chat.title')} showBack backTo="/support" />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={extractErrorMessage(query.error, t('support.chat.loadError'))}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  const user = query.data.user;
  const nameValue =
    user.full_name?.trim() ||
    [user.first_name, user.last_name].filter((p) => p?.trim()).join(' ').trim();
  const displayName = nameValue || user.email;

  const infoRows: { key: string; icon: typeof Mail; label: string; value: string }[] = [
    { key: 'name', icon: User, label: t('support.chat.info.name'), value: nameValue },
    { key: 'email', icon: Mail, label: t('support.chat.info.email'), value: user.email },
    {
      key: 'phone',
      icon: Phone,
      label: t('support.chat.info.phone'),
      value: user.phone_number?.trim() ?? '',
    },
  ].filter((row) => row.value.length > 0);

  const languages = user.languages.filter((l) => l.trim().length > 0);

  return (
    <>
      <div className="flex h-[calc(100vh-4rem)] flex-col">
        <PageHeader
          title={displayName}
          subtitle={user.email}
          showBack
          backTo="/support"
          sticky={false}
          className="shrink-0"
          right={
            isBlocked && canUnblock ? (
              <Button
                variant="success"
                leftIcon={<CircleCheck className="h-4 w-4" />}
                loading={unblock.isPending}
                onClick={() => unblock.mutate()}
              >
                {t('support.chat.unblock')}
              </Button>
            ) : !isBlocked && canBlock ? (
              <Button
                variant="danger"
                leftIcon={<Ban className="h-4 w-4" />}
                loading={block.isPending}
                onClick={() => setBlockConfirm(true)}
              >
                {t('support.chat.block')}
              </Button>
            ) : null
          }
        />

        <div className="flex min-h-0 flex-1">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4 lg:px-6"
            >
              {messages.length === 0 ? (
                <EmptyState
                  icon={<MessagesSquare className="h-8 w-8" />}
                  title={t('support.chat.emptyTitle')}
                  description={t('support.chat.emptyDescription')}
                />
              ) : (
                messages.map((m) => <FeaturesSupportChatMessageBubble key={m.id} message={m} />)
              )}
            </div>

            {canRespond && (
              <FeaturesSupportChatMessageComposer
                sending={send.isPending}
                onSend={(payload) => send.mutate(payload)}
              />
            )}
          </div>

          <aside className="hidden w-72 shrink-0 overflow-y-auto border-l border-slate-200 bg-white px-5 py-6 lg:block xl:w-80">
            <div className="flex flex-col items-center text-center">
              <span className="inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-muted">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
                ) : nameValue ? (
                  <span className="text-2xl font-semibold uppercase">{nameValue.charAt(0)}</span>
                ) : (
                  <User className="h-8 w-8" aria-hidden />
                )}
              </span>
              <p className="mt-3 break-words text-base font-semibold text-ink">{displayName}</p>
              {isBlocked && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                  <Ban className="h-3 w-3" aria-hidden />
                  {t('support.chat.blockedState')}
                </span>
              )}
            </div>

            <dl className="mt-6 space-y-4">
              {infoRows.map(({ key, icon: Icon, label, value }) => (
                <div key={key} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {label}
                    </dt>
                    <dd className="break-words text-sm text-ink-soft">{value}</dd>
                  </div>
                </div>
              ))}

              {languages.length > 0 && (
                <div className="flex items-start gap-3">
                  <Languages className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                  <div className="min-w-0">
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {t('support.chat.info.languages')}
                    </dt>
                    <dd className="mt-1 flex flex-wrap gap-1.5">
                      {languages.map((lang) => (
                        <span
                          key={lang}
                          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-soft"
                        >
                          {lang}
                        </span>
                      ))}
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          </aside>
        </div>
      </div>

      <ConfirmDialog
        open={blockConfirm}
        title={t('support.chat.confirmBlock.title')}
        description={t('support.chat.confirmBlock.description', { name: displayName })}
        confirmLabel={t('support.chat.confirmBlock.confirm')}
        confirmVariant="danger"
        loading={block.isPending}
        onCancel={() => setBlockConfirm(false)}
        onConfirm={() => block.mutate()}
      />
    </>
  );
};
